// src/pages/LoginPage.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { login as loginApi } from "../hooks/useApi";
import { useAuth } from "../hooks/useAuth";
import { CrookedStar, FourPointStar, Logo, TalentHero } from "../assets";

const WHATSAPP_NUMBER = "6288228773929";
const WHATSAPP_MESSAGE = encodeURIComponent(
  "Halo, saya ingin beli paket kelas untuk mendapatkan akun login Scholars Today."
);

const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const { user, login } = useAuth();
  const openWhatsApp = () => {
    window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer");
  };
  
  const handleLogin = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await loginApi({
        username,
        password,
      });

      login({
        user: res.user,
        token: res.token,
      });

      navigate("/IELTSTest");
    } catch (err) {
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      navigate("/IELTSTest", { replace: true });
    }
  }, [user, navigate]);

  return (
    <>
      {/* === VERSI MOBILE: Hanya Form Login === */}
      <div className="md:hidden min-h-fit st-surface-cream flex flex-col justify-between">
        {/* Form Login */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-sm rounded-2xl border st-border-login st-surface-login p-6 space-y-5 st-shadow-login">
            <h2 className="st-font-display text-xl font-bold st-text-brand">Welcome Back 👋</h2>
            <p className="st-font-body st-text-brand text-sm leading-relaxed">
              Today is a new day. It's your day. You shape it. Sign in to start managing your progress!
            </p>

            <div className="space-y-4">
              <div>
                <label className="block st-text-brand text-sm mb-1 font-medium">
                  Email
                </label>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  type="text"
                  placeholder="Example@gmail.com"
                  className="st-input-login"
                />
              </div>

              <div>
                <label className="block st-text-brand text-sm mb-1 font-medium">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="st-input-login pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-[#1e3e7e] transition"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                className="st-btn-login text-sm mt-2"
                onClick={handleLogin}
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>
              {error && <p className="text-red-600 text-sm">{error}</p>}

              {/* Link ke WhatsApp */}
              <button
                onClick={openWhatsApp}
                className="st-text-login-link text-sm text-center block w-full"
              >
                Forgot Password?
              </button>

              <p className="st-text-brand text-center text-sm">
                Don't you have an account?{" "}
                <button
                  onClick={openWhatsApp}
                  className="st-text-login-link font-medium"
                >
                  Contact Admin
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* === VERSI DESKTOP & TABLET: Card 2 Bagian === */}
      <div className="hidden md:flex min-h-screen st-surface-cream items-center justify-center p-6">
        <div className="w-full max-w-5xl rounded-[2.25rem] bg-white p-[14px] shadow-2xl">
          <div className="overflow-hidden rounded-[1.75rem] bg-white flex flex-col md:flex-row">
            {/* === KIRI: Hero Visual === */}
            <div className="relative w-full overflow-hidden bg-[#1F2A67] px-6 py-8 text-white md:w-1/2 md:px-8 lg:px-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(255,255,255,0.12)_0,transparent_28%),radial-gradient(circle_at_82%_72%,rgba(255,204,59,0.18)_0,transparent_22%),linear-gradient(135deg,#1F2A67_0%,#1A2258_52%,#141A43_100%)]" />
            <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:48px_48px]" />

            <img
              src={CrookedStar}
              alt=""
              aria-hidden="true"
              className="absolute left-[-16px] top-1/2 w-20 -translate-y-1/2 rotate-[-10deg] opacity-95 md:w-24"
            />
            <img
              src={FourPointStar}
              alt=""
              aria-hidden="true"
              className="absolute bottom-[-16px] right-[-16px] w-24 rotate-[-12deg] opacity-95 md:w-28"
            />

            <div className="relative z-10 flex h-full flex-col justify-between">
              <div className="flex items-start justify-between gap-3 text-[0.65rem] uppercase tracking-[0.3em] text-white/70 md:text-[0.7rem]">
                <span>don't miss out</span>
                <span className="text-right">scholars today</span>
              </div>

              <div className="mt-3 flex items-end justify-between gap-2 text-white/95 md:mt-3">
                <p className="text-sm font-medium md:text-base">
                  Make it <span className="font-serif italic text-white">Count</span>
                </p>
                <p className="text-sm font-medium md:text-base">
                  Aim <span className="font-serif italic text-white">Higher</span>
                </p>
              </div>

              <div className="mt-4 text-center">
                <h2 className="font-serif text-[clamp(1.8rem,3vw,2.6rem)] leading-[0.95] text-[#FFF6E4]">
                  A <span className="italic">Smarter</span> Way to
                </h2>

                <div className="relative mx-auto mt-4 w-fit">
                  <div className="absolute -inset-2 rounded-[1.6rem] bg-[#F3C94A]/95 blur-[0.5px]" />
                  <div className="relative -rotate-3 overflow-hidden rounded-[1.5rem] border-[8px] border-[#F5D569] shadow-[0_16px_40px_rgba(0,0,0,0.28)]">
                    <img
                      src={TalentHero}
                      alt="Student illustration"
                      className="h-[200px] w-[160px] object-cover object-center md:h-[260px] md:w-[200px]"
                    />
                  </div>
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 text-3xl font-black leading-none text-[#E84B4B] md:text-7xl">
                    ×
                  </div>
                </div>

                <div className="mt-4 font-serif text-[clamp(1.6rem,2.5vw,2.4rem)] leading-[0.95] text-[#FFF6E4]">
                  <div>Boost Your</div>
                  <div className="italic">IELTS Score</div>
                </div>
              </div>

              <div className="mt-4 flex items-end justify-between gap-3">
                <img
                  src={Logo}
                  alt="Scholars Today Logo"
                  className="h-8 w-auto object-contain md:h-[4rem]"
                />

                <div className="flex h-7 w-10 items-center justify-center rounded-full border border-white/65 bg-white/8 text-white/95 backdrop-blur-sm md:h-8 md:w-11">
                  <ArrowRight size={16} />
                </div>
              </div>
            </div>
            </div>

            {/* === KANAN: Form Login === */}
            <div className="st-surface-login border-t st-border-login p-6 md:border-l md:border-t-0 md:p-10 flex flex-col justify-center w-full md:w-1/2 space-y-5 st-shadow-login md:shadow-none">
              <h2 className="st-font-display text-xl md:text-2xl font-bold st-text-brand">Welcome Back 👋</h2>
              <p className="st-font-body st-text-brand text-sm md:text-base leading-relaxed">
                Today is a new day. It's your day. You shape it. Sign in to start managing your progress!
              </p>

              <div className="space-y-3 pt-2">
                <div>
                  <label className="block st-text-brand text-xs mb-1.5 font-medium">
                    Email
                  </label>
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    type="text"
                    placeholder="Example@gmail.com"
                    className="st-input-login text-sm"
                  />
                </div>

                <div>
                  <label className="block st-text-brand text-xs mb-1.5 font-medium">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="st-input-login pr-11 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((visible) => !visible)}
                      className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-[#1e3e7e] transition"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={openWhatsApp}
                    className="st-text-login-link text-xs font-medium"
                  >
                    Forgot Password?
                  </button>
                </div>

                <button
                  onClick={handleLogin}
                  disabled={loading}
                  className="st-btn-login text-sm mt-2"
                >
                  {loading ? "Signing in..." : "Sign in"}
                </button>
                {error && <p className="text-red-600 text-xs text-center">{error}</p>}

                <p className="st-text-brand text-center text-xs pt-1">
                  Don't you have an account?{" "}
                  <button
                    onClick={openWhatsApp}
                    className="st-text-login-link font-medium"
                  >
                    Contact Admin
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
